/**
 * A wire or a Zelle payment on an owner register line (TMMAPP-0133, TMMAPI-0165; Zelle and one
 * component for both rails, TMMAPP-0136 item 8 / TMMAPI-0170). `namesOwner` offers "This is my
 * account" (the add-account form, a wire's identifier prefilled, then the claim). Otherwise "Make it
 * a payee" confirms in place and posts `/me/payees/from-counterparty` for either rail
 * (TMMAPP-0137). `409 OWN_ACCOUNT_WIRE` switches to the own-account action; `409 NOT_A_WIRE` hides
 * it and asks for a refetch.
 * The confirm takes focus and returns it (the in-place pattern). In the register table (`md` up)
 * the parent holds which line is confirming and gives a full-width expansion row's cell: the
 * confirm renders there on one line (TMMAPP-0134 item 10). Elsewhere it stacks in place.
 *
 * The claim (TMMAPP-0137, TMMAPI-0172): "This is my account" claims by the line's own
 * `identityKey`, the key its "Add your account" item uses. A line without one (it names nobody)
 * never offers "This is my account": the claim would refuse it.
 *
 * TMMAPP-0138 item 7 (TMMAPI-0175): a same-bank transfer that names someone is a third rail. It
 * offers both answers, Make it a payee and (with a key) This is my account; one that names the
 * owner offers the own account first. "This is my account" is the shared own-account flow
 * (`OwnAccountDialog`): pick or add the account, then claim, with Try again after a refused claim.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { backendFields } from '@/api/errorDetails';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { payeesApi } from '@/api/payees';
import type { LineCounterparty } from '@/api/transactions';
import { transfersApi } from '@/api/transfers';
import { SubmitButton } from '@/components/SubmitButton';
import { translateServerPhrase } from '@/features/shared/useFieldErrors';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { prefillLast4, titleCaseWireName } from './identifierDisplay';
import { OwnAccountDialog } from './OwnAccountDialog';

function counterpartyName(counterparty: LineCounterparty): string {
  switch (counterparty.rail) {
    case 'WIRE':
      return counterparty.wire.counterpartyName ?? '';
    case 'ZELLE':
      return counterparty.zelle.counterpartyName ?? '';
    case 'TRANSFER':
      return counterparty.transfer.counterpartyName ?? '';
  }
}

function namesOwner(counterparty: LineCounterparty): boolean {
  switch (counterparty.rail) {
    case 'WIRE':
      return counterparty.wire.namesOwner;
    case 'ZELLE':
      return counterparty.zelle.namesOwner;
    case 'TRANSFER':
      return counterparty.transfer.namesOwner;
  }
}

function identityKey(counterparty: LineCounterparty): string | null {
  switch (counterparty.rail) {
    case 'WIRE':
      return counterparty.wire.identityKey;
    case 'ZELLE':
      return counterparty.zelle.identityKey;
    case 'TRANSFER':
      return counterparty.transfer.identityKey;
  }
}

/** The "future … will be named this too" sentence's key, per rail. */
const FUTURE_KEYS = {
  WIRE: 'wire.future',
  ZELLE: 'wire.futureZelle',
  TRANSFER: 'wire.futureTransfer',
} as const;

interface CounterpartyActionsProps {
  account: Account;
  transactionId: number;
  counterparty: LineCounterparty;
  operatingCurrency: string | null;
  onNotify: (message: string) => void;
  /** The line changed, or it was not a wire or Zelle line: the register refetches. */
  onChanged: () => void | Promise<void>;
  /** Table mode: the parent owns whether this line's confirm is open. */
  confirmOpen?: boolean | undefined;
  onConfirmOpenChange?: ((open: boolean) => void) | undefined;
  /** Table mode: the expansion row's cell the confirm renders into. */
  confirmContainer?: HTMLElement | null | undefined;
}

export function CounterpartyActions({
  account,
  transactionId,
  counterparty,
  operatingCurrency,
  onNotify,
  onChanged,
  confirmOpen,
  onConfirmOpenChange,
  confirmContainer,
}: CounterpartyActionsProps) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const ownRef = useRef<HTMLButtonElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [hidden, setHidden] = useState(false);
  const key = identityKey(counterparty);
  const [ownOffer, setOwnOffer] = useState(key !== null && namesOwner(counterparty));
  const [localConfirming, setLocalConfirming] = useState(false);
  const inRow = onConfirmOpenChange !== undefined;
  const confirming = inRow ? confirmOpen === true : localConfirming;
  const setConfirming = inRow ? onConfirmOpenChange : setLocalConfirming;
  const [dialog, setDialog] = useState(false);
  const served = counterpartyName(counterparty);
  const [name, setName] = useState(() => titleCaseWireName(served));
  const [nameError, setNameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const returnFocus = useRef(false);
  const wire = counterparty.rail === 'WIRE' ? counterparty.wire : null;
  const transfer = counterparty.rail === 'TRANSFER' ? counterparty.transfer : null;
  // A named transfer offers both answers (item 7); a wire or Zelle line one at a time.
  const both = transfer !== null && key !== null;
  const who = wire?.identifierDisplay ?? (served || name);

  useEffect(() => {
    if (confirming) {
      nameRef.current?.focus();
      return;
    }
    if (returnFocus.current) {
      returnFocus.current = false;
      buttonRef.current?.focus();
    }
  }, [confirming, ownOffer, confirmContainer]);

  function closeConfirm() {
    setConfirming(false);
    setNameError(null);
    returnFocus.current = true;
  }

  async function handleCreatePayee() {
    if (busy) return;
    setBusy(true);
    setNameError(null);
    try {
      const trimmed = name.trim();
      const chosen = trimmed.length > 0 ? trimmed : null;
      const created = await payeesApi.fromCounterparty(transactionId, chosen);
      const label = created.name.length > 0 ? created.name : trimmed;
      closeConfirm();
      onNotify(t('wire.created', { name: label, count: created.linesResolved }));
      await onChanged();
    } catch (error) {
      // Without a key the claim refuses the line, so the own-account action is never offered.
      if (isApiError(error) && error.code === ErrorCodes.OWN_ACCOUNT_WIRE && key !== null) {
        setConfirming(false);
        setOwnOffer(true);
        returnFocus.current = true;
      } else if (isApiError(error) && error.code === ErrorCodes.NOT_A_WIRE) {
        setHidden(true);
        setConfirming(false);
        await onChanged();
      } else if (isApiError(error) && error.code === ErrorCodes.VALIDATION_FAILED) {
        const fields = backendFields(error);
        setNameError(
          fields.name !== undefined
            ? translateServerPhrase(fields.name, t)
            : t('errors.fieldInvalid'),
        );
      } else {
        setNameError(errorCopy(t, error, 'accounts.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  // One gate for both actions: the button names its action in `data-missing-action`.
  const gate = useSubscriberAction((event) => {
    if (event.currentTarget.dataset.missingAction === 'own') setDialog(true);
    else setConfirming(true);
  });

  if (hidden) return null;

  const nameField = (
    <TextField
      inputRef={nameRef}
      size="small"
      label={t('wire.nameLabel')}
      value={name}
      onChange={(event) => setName(event.target.value)}
      error={nameError !== null}
      helperText={nameError ?? undefined}
      fullWidth={!inRow}
      sx={inRow ? { width: 280, flexShrink: 0 } : undefined}
    />
  );
  const futureWho = who.length > 0 ? who : name;
  const note = (
    <Typography variant="caption" sx={{ color: 'text.secondary', flex: inRow ? 1 : undefined }}>
      {t(FUTURE_KEYS[counterparty.rail], { who: futureWho })}
    </Typography>
  );
  const actions = (
    <Stack
      direction="row"
      sx={{ gap: 1, flexWrap: 'wrap', marginInlineStart: inRow ? 'auto' : undefined }}
    >
      <Button size="small" onClick={closeConfirm} disabled={busy}>
        {t('common.cancel')}
      </Button>
      <SubmitButton size="small" loading={busy} onClick={() => void handleCreatePayee()}>
        {t('wire.create')}
      </SubmitButton>
    </Stack>
  );
  const confirm = inRow ? (
    <Stack
      direction="row"
      role="group"
      aria-label={t('wire.makePayee')}
      sx={{
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        // Pinned to the register's visible width while the wide table scrolls under it.
        position: 'sticky',
        insetInlineStart: 16,
        width: 'calc(100cqi - 32px)',
      }}
    >
      {nameField}
      {note}
      {actions}
    </Stack>
  ) : (
    <Stack
      spacing={1}
      role="group"
      aria-label={t('wire.makePayee')}
      sx={{ mt: 0.5, maxWidth: 360 }}
    >
      {nameField}
      {note}
      {actions}
    </Stack>
  );

  const ownButton = (
    <Button
      ref={ownRef}
      size="small"
      data-missing-action="own"
      aria-describedby={gate.descriptionId}
      onClick={gate.run}
      sx={{ px: 0.5 }}
    >
      {t('wire.thisIsMine')}
    </Button>
  );
  const payeeButton = (
    <Button
      ref={buttonRef}
      size="small"
      data-missing-action="payee"
      aria-describedby={gate.descriptionId}
      onClick={gate.run}
      sx={{ px: 0.5 }}
    >
      {t('wire.makePayee')}
    </Button>
  );

  return (
    <Box data-wire-actions="" onClick={(event) => event.stopPropagation()}>
      {!confirming && !both && (
        <Button
          ref={buttonRef}
          size="small"
          data-missing-action={ownOffer ? 'own' : 'payee'}
          aria-describedby={gate.descriptionId}
          onClick={gate.run}
          sx={{ px: 0.5 }}
        >
          {ownOffer ? t('wire.thisIsMine') : t('wire.makePayee')}
        </Button>
      )}
      {!confirming && both && (
        // Names the owner: the own account first; otherwise the payee.
        <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
          {ownOffer ? ownButton : payeeButton}
          {ownOffer ? payeeButton : ownButton}
        </Stack>
      )}
      {confirming && !inRow && confirm}
      {confirming &&
        inRow &&
        confirmContainer != null &&
        createPortal(
          <Box data-wire-confirm="" onClick={(event) => event.stopPropagation()}>
            {confirm}
          </Box>,
          confirmContainer,
        )}
      <SubscriberActionHint id={gate.descriptionId} description={gate.description} />
      {dialog && key !== null && (
        <OwnAccountDialog
          title={t('wire.thisIsMine')}
          start={{ kind: 'choose' }}
          source={account}
          mask={transfer?.mask ?? null}
          operatingCurrency={operatingCurrency}
          form={{
            initialIdentifier: wire?.identifier ?? '',
            initialInstitution: wire?.bank ?? '',
            initialLast4: prefillLast4(transfer?.mask, wire?.identifier),
          }}
          // The line's own key: the claim classifies every line of that identity.
          claim={(target) => transfersApi.claimIdentity(target.id, key, true)}
          onNotify={onNotify}
          onClose={() => {
            setDialog(false);
            (ownRef.current ?? buttonRef.current)?.focus();
          }}
          onClaimed={(result) => {
            setDialog(false);
            onNotify(t('review.missingAccount.transfersNow', { count: result.classified }));
            void onChanged();
          }}
        />
      )}
    </Box>
  );
}

/** The rail's facts-box tag. */
const TAG_KEYS = {
  WIRE: 'wire.tag',
  ZELLE: 'wire.zelleTag',
  TRANSFER: 'wire.transferTag',
} as const;

/**
 * The facts-box tag: "Wire", "Zelle" or "Transfer", then the counterparty (and a wire's identifier
 * and bank, a transfer's "ending {mask}").
 */
export function CounterpartyTag({ counterparty }: { counterparty: LineCounterparty }) {
  const { t } = useTranslation();
  const rail = (): Array<string | null> => {
    switch (counterparty.rail) {
      case 'WIRE':
        return [
          counterparty.wire.counterpartyName,
          counterparty.wire.identifierDisplay,
          counterparty.wire.bank,
        ];
      case 'ZELLE':
        return [counterparty.zelle.counterpartyName];
      case 'TRANSFER': {
        const { counterpartyName: named, mask } = counterparty.transfer;
        return [named, mask !== null ? t('wire.endingMask', { mask }) : null];
      }
    }
  };
  const bits = rail().filter((bit): bit is string => bit !== null && bit.length > 0);
  return (
    <Stack direction="row" sx={{ gap: 0.75, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Typography component="span" variant="micro">
        {t(TAG_KEYS[counterparty.rail])}
      </Typography>
      {bits.length > 0 && (
        <Typography component="span" variant="body2">
          {bits.join(' · ')}
        </Typography>
      )}
    </Stack>
  );
}
